/**
 * International Auctions — model contract.
 *
 * PROTOTYPE: these interfaces are the contract the real API will fill. Field
 * names mirror the high-level design §6.6 exactly, so that swapping the mock
 * service for `HttpClient` changes nothing in any component.
 *
 * Design:  docs/external-cataloge/phase-1/international-auctions-high-level.md
 * Names:   docs/external-cataloge/phase-1/settings.md
 * Ticket:  BXS-3627
 *
 * Nothing in this file may import from Angular, RxJS or a fixture. It is pure
 * types plus pure functions, so both the public app and the admin app can hold
 * the same understanding of the data.
 */

/* ------------------------------------------------------------------ *
 * Primitives
 * ------------------------------------------------------------------ */

/** The three partner houses in the prototype. Real data is open-ended. */
export type ExternalHouseSlug = 'tattersalls' | 'arqana' | 'fasigtipton';

/** Drives the badge and the sort order on the index, house page and band. */
export type ExternalSaleStatus = 'live' | 'upcoming' | 'preview' | 'closed';

/**
 * Lot outcome. Carries the outcome ON ITS OWN — a price never implies it.
 * Tattersalls publish `Lot Not Sold` alongside `4,500 gns` (design §6.1).
 */
export type ExternalLotStatus =
  | 'catalogued'
  | 'withdrawn'
  | 'sold'
  | 'not_sold'
  | 'private_sale';

/** Tattersalls sell in guineas: 1 gn = GBP 1.05. Never render those as GBP. */
export type ExternalPriceUnit = 'currency' | 'guineas';

/** Mirrors HorseDocumentCategoryLock. `null` = public, which is v1 everywhere. */
export type ExternalDocumentLock = 'login_required' | 'accepted_buyer' | null;

/** Only these two locales have translation files in this app. */
export type SupportedLocale = 'en' | 'ar';

/**
 * A translated string. `ar` is routinely missing — houses supply English only,
 * and that must render in English rather than blank (design §6.5).
 */
export interface TranslatedText {
  en?: string | null;
  ar?: string | null;
}

/* ------------------------------------------------------------------ *
 * Entities
 * ------------------------------------------------------------------ */

/**
 * A partner auction house.
 *
 * `name` is NOT translated — Tattersalls is a proper noun. `lot_label` and
 * `consignor_label` are DATA, not i18n keys: Fasig-Tipton catalogue by Hip,
 * Arqana by Vendor, and no amount of translation makes that a locale concern.
 */
export interface ExternalHouse {
  id: number;
  slug: string;
  name: string;
  description: TranslatedText;
  country_code: string;
  country_name: TranslatedText;
  base_venue: TranslatedText;
  website_url: string;
  logo_url: string;
  /**
   * The logo artwork already carries the house colour — Tattersalls' mark is
   * gold on solid navy — so the white plaque behind it has to come off or it
   * shows as a framed box sitting on a band of the same navy.
   */
  logo_on_brand?: boolean;
  /** The co-branded band's background. */
  brand_color: string;
  /** Readable ink on top of `brand_color`. */
  brand_color_contrast: string;
  /**
   * The house's SECOND colour: the eyebrow, the outbound button, and the rule
   * closing the band. Optional — a house without one falls back to its contrast
   * ink, which is always legible, rather than to an invented gold.
   */
  brand_accent_color?: string;
  /** Readable ink on top of `brand_accent_color`. Not derivable from it. */
  brand_accent_contrast?: string;
  /** 'Lot' | 'Hip' */
  lot_label: string;
  /** 'Lots' | 'Hips' */
  lot_label_plural: string;
  /** 'Consignor' | 'Vendor' */
  consignor_label: string;
  default_currency_code: string;
  default_price_unit: ExternalPriceUnit;
  is_published: boolean;
  sort_order: number;
}

/** Lookup populated implicitly by the admin type-ahead (design §6.4). */
export interface ExternalSaleType {
  id: number;
  slug: string;
  name: TranslatedText;
}

export interface ExternalSale {
  id: number;
  house_id: number;
  house_slug: string;
  slug: string;
  /** The house's own key. The join key for a future feed (design §6.1). */
  external_id: string;
  name: TranslatedText;
  sale_type_id: number | null;
  sale_type: TranslatedText | null;
  /** ISO date, `YYYY-MM-DD`. */
  starts_on: string;
  /** ISO date, `YYYY-MM-DD`. Inclusive — a sale is live on its last day. */
  ends_on: string;
  timezone: string;
  venue: TranslatedText;
  country_code: string;
  currency_code: string;
  price_unit: ExternalPriceUnit;
  /** What the house says the catalogue holds. Drives the completeness gap. */
  declared_lot_count: number;
  catalogue_url: string | null;
  registration_url: string | null;
  results_url: string | null;
  cover_image_url: string | null;
  description: TranslatedText;
  /**
   * Set only for the genuine exceptions — a postponed sale, or a catalogue
   * published months early that should read Preview (design §6.3).
   * `null` means the status is computed from the dates.
   */
  status_override: ExternalSaleStatus | null;
  is_published: boolean;
}

/**
 * A sale with everything a card or header needs, resolved.
 * The service returns this; components never derive status themselves.
 */
export interface ExternalSaleView extends ExternalSale {
  status: ExternalSaleStatus;
  status_is_overridden: boolean;
  house: ExternalHouse;
  /** How many lots actually exist in the data, vs `declared_lot_count`. */
  entered_lot_count: number;
}

export interface ExternalLotPhoto {
  id: number;
  url: string;
  thumb_url: string;
  /**
   * The photograph's own pixel size, recorded at import.
   *
   * The stage is sized from this so nothing is cropped: 155 of the first
   * Tattersalls sale's 377 photographs are PORTRAIT, and a portrait shot in a
   * fixed landscape box loses half of itself to a centred crop — the horse's
   * head and legs, which is the half that matters.
   *
   * `null` for a row that holds only the house's URL (mirroring off, or a file
   * missing from the bucket). The stage falls back to its fixed ratio there.
   */
  width: number | null;
  height: number | null;
  /** Free text — "Walking", "Near side". No taxonomy (design §7.2). */
  caption: string | null;
  is_primary: boolean;
  order: number;
}

export interface ExternalLotVideo {
  id: number;
  /** Both are in v1 from day one (design §7.4). */
  kind: 'external' | 'internal';
  /** `null` when `kind === 'internal'`. */
  provider: 'youtube' | 'vimeo' | null;
  url: string;
  /** Internal video has no platform poster — admin supplies one. */
  poster_url: string | null;
  title: string | null;
}

/**
 * One locale's actual bytes for a document.
 *
 * A document is the LOGICAL row — its category, its access level, its date. The
 * file is what a visitor opens, and a translated document holds one per locale,
 * which is why the name, the mime and the size live here and not on the parent:
 * an Arabic pedigree is a different PDF of a different size.
 */
export interface ExternalLotDocumentFile {
  file_name: string;
  /**
   * The address, under BOTH names it arrives with: inside `files` the API calls
   * it `url`, while the document one level up calls the same thing `file_url` —
   * and that flat document doubles as the single English file for a house feed
   * that has no translations, so both spellings have to resolve.
   *
   * Null or absent means this locale offers nothing to open. A locked document
   * still lists its files, with the address withheld rather than the row: that
   * there IS a vet certificate is public, the bytes are not.
   */
  url?: string | null;
  file_url?: string | null;
  mime: string;
  size_bytes: number;
}

export interface ExternalLotDocument {
  id: number;
  category_id: number;
  category_name: TranslatedText;
  file_name: string;
  file_url: string;
  /** ISO date. */
  file_date: string | null;
  mime: string;
  size_bytes: number;
  lock_type: ExternalDocumentLock;
  /**
   * The house published, or we commissioned, this document in more than one
   * language. It is set PER DOCUMENT, not per category — a house may translate
   * the pedigree of one lot and not the next.
   *
   * It does NOT decide whether the language toggle renders: a document marked
   * translatable whose Arabic file has not been produced yet still holds only
   * one file, and a toggle offering a language that does not exist is worse
   * than no toggle. `files` decides that, and this flag is what the admin
   * worklist counts as "awaiting Arabic".
   */
  is_translatable?: boolean;
  /**
   * The bytes, keyed by locale. Absent for a house feed that has no notion of
   * translated documents — the flat `file_*` fields above are then the single
   * file, which is exactly what a non-translatable document is.
   */
  files?: Partial<Record<SupportedLocale, ExternalLotDocumentFile>>;
  /**
   * True when `file_url` opens the house's own system instead of returning a
   * file — Tattersalls' Repository is the case this exists for: one link per
   * lot to their X-rays, scope videos and lab results.
   *
   * It is still a document. It has a category, an access level and a place in
   * the documents tab like any other row. What it does NOT have is bytes, so
   * the row offers "Open" rather than "Download" and never renders a preview:
   * a third-party portal cannot be framed, and promising a viewer we cannot
   * deliver is worse than sending the visitor to the house.
   *
   * Absent means false. A house feed that has no notion of this just works.
   */
  is_link?: boolean;
}

/** The house's dated catalogue amendments. A timeline, not a field (§6.6). */
export interface ExternalLotUpdate {
  id: number;
  /** ISO date. */
  published_on: string;
  body: string;
}

/** An unmapped label→value pair. The safety net that avoids a migration. */
export interface ExternalLotDetail {
  label: string;
  value: string;
}

export interface ExternalLot {
  id: number;
  sale_id: number;

  /** String, never an integer — `0026` and `26A` both exist (design §6.1). */
  lot_number: string;
  lot_number_sort: number;

  /** Nullable. Unnamed lots are normal. */
  name: string | null;
  /**
   * Stored, not derived. The name where there is one, otherwise the house's
   * own fallback: `Siyouni (FR) / Fabulous (IRE) B.F. (IRE)`.
   */
  display_title: string;
  description: string;

  sire: string;
  dam: string;
  sire_of_dam: string | null;
  color: string | null;
  sex: string | null;
  /** The published compound code, verbatim: `B.M.`, `Gr.M.`, `B.F.` */
  sex_color_code: string | null;
  /** A year, never a date — the house publishes `2021` and nothing more. */
  year_foaled: number | null;
  foaled_in: string | null;

  sale_day: string | null;
  horse_type: string | null;

  consignor: string;
  consignor_contact_name: string | null;
  consignor_contact_phone: string | null;
  consignor_contact_email: string | null;
  stabling: string | null;
  covered_by: string | null;
  vat_status: string | null;

  /* --- Timed online sales -------------------------------------------------
   * Surveyed from Tattersalls' RING sales, the fields above cannot express an
   * online sale: there, every lot closes at its own time, Timeform rate them,
   * and the horse stands somewhere a buyer has to travel to. All OPTIONAL — a
   * catalogue sale publishes none of them, and a house feed that has never
   * heard of them just works.
   * --------------------------------------------------------------------- */

  /**
   * ISO 8601 datetime. THE headline fact of a timed sale: lots close minutes
   * apart, so this is the deadline for this lot alone, not the sale's.
   */
  bidding_ends_at?: string | null;
  /** Timeform's rating, roughly 0-140. A quality signal, not a detail row. */
  timeform_rating?: number | null;
  /** Where the horse physically stands, as the house publishes it. */
  location_address?: string | null;
  /** ISO 3166-1 alpha-2. It decides the paperwork, so it is its own field. */
  location_country_code?: string | null;
  /** "Whole Sale" / "Feature HIT Session" — a real split within one sale. */
  session_label?: string | null;
  /** A string, not a number: hands are `16.1`, which is 16 hands 1 inch. */
  height_hands?: string | null;
  /** ISO date. A young horse is still growing, so an old measure is weaker. */
  height_measured_on?: string | null;
  /**
   * Vices and declarations. EMPTY IS MEANINGFUL: "nothing declared" is a
   * disclosure the house made, not a field it left out, and it renders as one.
   * Only `undefined` — a house that does not publish declarations at all —
   * means there is nothing to say.
   */
  declarations?: string | null;

  status: ExternalLotStatus;
  purchaser: string | null;
  /** The published string unparsed, where the house conflates three facts. */
  purchaser_raw: string | null;

  /** Minor units. Never `0` as a stand-in for "no price". */
  price_minor: number | null;
  price_currency_code: string | null;
  price_unit: ExternalPriceUnit | null;
  /** The house's own formatting, where they publish one. */
  price_display: string | null;

  /**
   * The outbound target. Never linked, and never sent to the redirect either —
   * the server reads it off this same row, which is the only copy anyone can
   * trust (§8.3).
   */
  source_url: string;

  tags: string[];
  raw_details: ExternalLotDetail[];
  is_published: boolean;

  photos: ExternalLotPhoto[];
  videos: ExternalLotVideo[];
  documents: ExternalLotDocument[];
  updates: ExternalLotUpdate[];
}

/** A lot with its sale and house resolved, for the lot profile page. */
export interface ExternalLotView extends ExternalLot {
  sale: ExternalSaleView;
  /** For prev/next navigation. `null` at either end of the catalogue. */
  previous_lot_number: string | null;
  next_lot_number: string | null;
}

/**
 * One row of the lot-number index — the whole catalogue's numbers and nothing
 * else, behind the "jump to lot" box (CEO review, 18 Aug 2026).
 *
 * Deliberately NOT `Pick<ExternalLot, …>`: the endpoint that fills this reads
 * four columns and eager-loads nothing, and typing it off the full lot would
 * invite somebody to reach for a field that is not in the payload.
 */
export interface ExternalLotNumber {
  lot_number: string;
  /** The catalogue title, shown beside the number in the suggestion list. */
  title: string;
  /** `withdrawn` is tagged in the list; the lot still has a page that says so. */
  status: ExternalLotStatus;
}

/* ------------------------------------------------------------------ *
 * Queries
 * ------------------------------------------------------------------ */

export interface SaleQuery {
  house?: string | null;
  status?: ExternalSaleStatus | null;
  sale_type?: string | null;
}

export interface LotQuery {
  /** Matches `lot_number` exactly, case-insensitive. */
  lot_number?: string | null;
  /** Matches name, sire or dam. */
  search?: string | null;
  sex?: string | null;
  year_foaled?: number | null;
  horse_type?: string | null;
  consignor?: string | null;
  sale_day?: string | null;
  page?: number;
  per_page?: number;
}

export interface Paginated<T> {
  items: T[];
  page: number;
  per_page: number;
  total: number;
  total_pages: number;
}

/* ------------------------------------------------------------------ *
 * Pure helpers — one implementation, shared by every surface
 * ------------------------------------------------------------------ */

/**
 * The platform's existing fallback chain, copied verbatim from
 * `HomepageSlidesCacheService::getTranslationValue()`:
 *
 *     requested locale  →  English  →  any available translation  →  null
 *
 * Empty and whitespace-only strings count as missing, not as a value. This is
 * why a house that supplies English only renders in English on the Arabic site
 * rather than rendering blank.
 */
export function resolveTranslation(
  value: TranslatedText | null | undefined,
  locale: SupportedLocale
): string | null {
  if (!value) {
    return null;
  }
  const candidates: Array<string | null | undefined> = [
    value[locale],
    value.en,
    ...Object.values(value),
  ];
  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim().length > 0) {
      return candidate;
    }
  }
  return null;
}

/** Midnight UTC for a `YYYY-MM-DD` string. Keeps SSR and the browser aligned. */
export function parseIsoDate(value: string): number {
  return Date.parse(`${value}T00:00:00Z`);
}

/** Midnight UTC today, so server and client agree on which day it is. */
export function startOfTodayUtc(now: Date = new Date()): number {
  return Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
}

/**
 * Sale status, derived from the dates (design §6.3).
 *
 * `status_override` wins where it is set — that is the whole point of it. The
 * derivation itself yields only live / upcoming / closed; `preview` is reached
 * exclusively through the override, which is exactly the case §6.3 describes
 * ("a catalogue published months early that should read Preview").
 *
 * `ends_on` is inclusive: a sale is still live on its final day.
 */
export function deriveSaleStatus(
  sale: Pick<ExternalSale, 'starts_on' | 'ends_on' | 'status_override'>,
  now: Date = new Date()
): ExternalSaleStatus {
  if (sale.status_override) {
    return sale.status_override;
  }
  const today = startOfTodayUtc(now);
  if (today > parseIsoDate(sale.ends_on)) {
    return 'closed';
  }
  if (today >= parseIsoDate(sale.starts_on)) {
    return 'live';
  }
  return 'upcoming';
}

/** live → upcoming → preview → closed, per the mockup. */
export const SALE_STATUS_RANK: Record<ExternalSaleStatus, number> = {
  live: 0,
  upcoming: 1,
  preview: 2,
  closed: 3,
};

/**
 * The mockup's sort: by status rank, then soonest first — except closed sales,
 * which read most-recently-finished first.
 */
export function compareSalesForDisplay(
  a: ExternalSaleView,
  b: ExternalSaleView
): number {
  const rank = SALE_STATUS_RANK[a.status] - SALE_STATUS_RANK[b.status];
  if (rank !== 0) {
    return rank;
  }
  if (a.status === 'closed') {
    return parseIsoDate(b.ends_on) - parseIsoDate(a.ends_on);
  }
  return parseIsoDate(a.starts_on) - parseIsoDate(b.starts_on);
}

/** Minor units → major. Guineas and every currency here are 100-minor. */
export function toMajorUnits(priceMinor: number): number {
  return priceMinor / 100;
}

const CURRENCY_SYMBOLS: Record<string, string> = {
  GBP: '£',
  EUR: '€',
  USD: '$',
  AED: 'AED ',
  SAR: 'SAR ',
};

/**
 * Renders a lot price the way the house publishes it.
 *
 * Guineas render as `4,500 gns` — NEVER as `£4,500`. A guinea is GBP 1.05, so
 * the pound rendering is wrong by 5% and is the single field most likely to
 * embarrass us in front of a partner (design §6.1).
 *
 * Returns `null` when there is no price, which is a different thing from a
 * price of zero and a different thing again from "not sold".
 */
export function formatLotPrice(
  lot: Pick<
    ExternalLot,
    'price_minor' | 'price_currency_code' | 'price_unit' | 'price_display'
  >,
  locale: SupportedLocale = 'en'
): string | null {
  if (lot.price_display && lot.price_display.trim().length > 0) {
    return lot.price_display;
  }
  if (lot.price_minor === null || lot.price_minor === undefined) {
    return null;
  }
  const amount = toMajorUnits(lot.price_minor);
  const grouped = new Intl.NumberFormat(locale === 'ar' ? 'ar-EG' : 'en-GB', {
    maximumFractionDigits: 0,
  }).format(amount);

  if (lot.price_unit === 'guineas') {
    return `${grouped} gns`;
  }
  const symbol = CURRENCY_SYMBOLS[lot.price_currency_code ?? ''] ?? '';
  return symbol ? `${symbol}${grouped}` : `${grouped} ${lot.price_currency_code ?? ''}`.trim();
}

/**
 * True when the lot has nothing to show in the media stage. This is the NORMAL
 * state — neither surveyed Tattersalls sale published a single photo or video
 * (design Appendix B.4), so the placeholder is a first-class state, not an
 * error state.
 */
export function hasNoMedia(
  lot: Pick<ExternalLot, 'photos' | 'videos'>
): boolean {
  return lot.photos.length === 0 && lot.videos.length === 0;
}

/**
 * The token the outbound CTA points at. The server decodes it, logs the click
 * and 302s to the house with UTM attached (design §8.3), so this format is a
 * contract with the API rather than a local convenience.
 */
export function buildGoToken(
  kind: 'lot' | 'registration',
  houseSlug: string,
  saleSlug: string,
  lotNumber?: string
): string {
  return [kind, houseSlug, saleSlug, lotNumber].filter(Boolean).join('~');
}

/* ==================================================================== *
 * The buyer's own shortlist and notes (BXS-3627, M19)
 *
 * Everything below is LOGGED-IN ONLY. The catalogue above stays open to
 * guests — that was the whole shape of Saeed's answer to Mai's objection
 * in the 18 August review, and nothing here may leak into a guest path.
 * ==================================================================== */

/**
 * The buyer's grade for a lot. Values are the platform's own
 * `HorseRatingEnum`, shared verbatim with the notes on our native auctions:
 * a buyer who grades a horse `A+` at one of our sales means the same thing
 * grading a Tattersalls lot `A+`.
 */
export const LOT_RATINGS = ['A+', 'A', 'B+', 'B', 'C', 'D'] as const;
export type LotRating = (typeof LOT_RATINGS)[number];

/** `HorseConfirmationEnum`, shared with the native notes. */
export const LOT_CONFORMATIONS = [
  'Balanced',
  'Great Walk',
  'Strong Hip',
  'Offset Knees',
  'Back at Knees',
  'Toes In',
  'Toes Out',
  'Weak Hocks',
  'Long Pasterns',
  'Straight Pasterns',
] as const;

/** `HorseScopeEnum`, shared with the native notes. */
export const LOT_SCOPES = [
  'Scope 1',
  'Scope 2A',
  'Scope 2B',
  'Scope 3A',
  'Scope 3B',
  'Scope other',
  'X-Ray (Normal)',
  'X-Ray (Other)',
] as const;

/** One buyer's private note on one lot. */
export interface LotNote {
  id: number;
  lot_id: number;
  rating: LotRating | null;
  notes: string;
  confirmation_type: string[];
  scope_type: string[];
  updated_at: string | null;
}

/**
 * What a catalogue card needs to know about a lot to badge it correctly.
 *
 * `has_note` is the server's answer, not `note !== null`: a note row can exist
 * holding nothing at all, and a buyer who cleared their text must stop seeing
 * the marker that says they wrote some.
 */
export interface LotState {
  lot_id: number;
  is_favourite: boolean;
  has_note: boolean;
  rating: LotRating | null;
}

/**
 * One row of "my international list".
 *
 * Carries the three slugs the lot URL is built from rather than a path,
 * because the frontend owns its own routing and its URLs are locale-prefixed.
 */
export interface MyLotRow {
  id: number;
  house_slug: string;
  sale_slug: string;
  lot_number: string;
  house_name: TranslatedText;
  sale_name: TranslatedText;
  /** The HOUSE's word — `Lot` at Tattersalls, `Hip` at Fasig-Tipton. */
  lot_label: string;
  display_title: string;
  name: string | null;
  sire: string;
  dam: string;
  sex: string;
  year_foaled: number | null;
  consignor: string;
  price_display: string | null;
  status: string | null;
  photo_url: string | null;
  /**
   * False once a lot leaves the catalogue. The row stays in the list and says
   * so rather than vanishing — the buyer chose it, and their note is attached
   * to it.
   */
  is_published: boolean;
  favourited_at: string | null;
  note: LotNote | null;
}
